import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import Layout from "src/components/program/layout/Layout";
import Button from "src/components/app/Button";
import SEO from "src/components/common/SEO";

import supportPrograms from "src/data/app/programs";
import { getUrlParams } from "src/helpers/common";

import frameIllustration from "src/assets/images/common/Frame.png";
import auth from "src/helpers/auth";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import { sendMessageToParent } from "src/helpers/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import { isInApp } from "src/helpers/parentMessageHandler";
import environment from "src/constants/environment";

const ExistingUserScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const queryParams = getUrlParams() as Record<string, string>;
  const appName =
    ((queryParams as any)?.appName as "GOODFLIP" | "GOODFLIP_CARE") ||
    "GOODFLIP_CARE";
  const config =
    supportPrograms[params.support_program_key as keyof typeof supportPrograms];

  useEffect(() => {
    // Event: EXISTING_USER_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.EXISTING_USER_SCREEN);

    // Cleanup function - runs when component unmounts (user leaves the screen)
    return () => {
      (async () => {
        await auth.handleLogout();
      })();
    };
  }, []);

  const redirectToAppDownloadScreen = () => {
    if (isInApp()) {
      sendMessageToParent(POST_MESSAGE_EVENTS.OPEN_EXTERNAL_LINK, {
        link: `${environment.DOMAIN_URL}/app-links/app-download?appName=${appName}_APP`,
      });
      return;
    } else {
      navigate("/app-links/app-download?appName=" + appName + "_APP");
    }
  };

  const title =
    appName === "GOODFLIP" ? "GoodFlip user!" : "GoodFlip Care user!";
  const description =
    appName === "GOODFLIP"
      ? "You are already a GoodFlip user. Please download the app to continue."
      : "You are already a GoodFlip Care user. Please download the app to continue.";

  return (
    <Layout
      enableTheme={true}
      className="min-h-screen bg-gradient-to-b from-[#D1FADF] to-white"
      supportProgramKey={params.support_program_key}
      footer={
        <div className="bg-white border-t border-gray-200 p-4">
          <Button fullWidth onClick={redirectToAppDownloadScreen}>
            <span className="flex items-center gap-2">
              Download App
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
        </div>
      }
      stickyFooter={true}
    >
      <SEO title={title} description={description} />
      <div className="flex flex-col h-full">
        <div className="p-4 flex-1 overflow-y-auto flex flex-col items-center">
          {config?.logo_url && (
            <img
              src={config.logo_url}
              alt={config.support_program_name}
              className="h-10 mb-8"
            />
          )}

          {/* Title */}
          <h1 className="mt-6 font-serif text-5xl font-medium text-[var(--text-primary)] max-w-sm leading-tight tracking-[-0.01em]">
            Single Program Access Only
          </h1>

          {/* Description */}
          <p className="mt-4 text-base max-w-sm leading-relaxed">
            Since you're already enrolled in the{" "}
            <span className="font-semibold">
              {appName === "GOODFLIP"
                ? "GoodFlip Metabolic Care Program"
                : config?.support_program_name || "GoodFlip Care Program"}
            </span>
            , access to other programs is not available. Please continue your
            current program.
          </p>

          {/* Illustration */}
          <div className="mt-20 w-full flex justify-center">
            <img
              src={frameIllustration} // <-- you’ll replace this
              alt="Program illustration"
              className="max-w-xs w-full"
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ExistingUserScreen;
